"use client";

import { Slot } from "radix-ui";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useDidMount } from "rooks";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import { getIsMobile } from "../helpers";

// TODO - Rename component cuz it's not only fade effect; Animation is flexed/none;
// TODO - Create fallback for critical cases (e.g. unsupported observers)

type ClassVariantsProps = VariantProps<typeof getFadeInIntersectionClass>;

const getFadeInIntersectionClass = tv({
  base: "group/intersected",
  variants: {
    inView: {
      true: "animate-intersected-in",
    },
    animation: {
      none: "",
      fade: "opacity-0",
      fadeUp: "opacity-0",
    },
  },
  defaultVariants: {
    inView: false,
    animation: "fade",
  },
  compoundVariants: [
    {
      inView: true,
      animation: "fade",
      class: "animate-duration-500 animate-ease-out animate-fade animate-fill-forwards",
    },
    {
      inView: true,
      animation: "fadeUp",
      class: "animate-duration-500 animate-ease-out animate-fade-up animate-fill-forwards",
    },
  ],
});

type FadeInIntersectionProps = {
  readonly children: ReactNode;
  readonly threshold?: number;
  readonly thresholdMobile?: number;
  readonly className?: string;
  readonly rootMargin?: string;
  readonly style?: Record<string, string>;
  readonly asChild?: boolean;
  readonly delay?: number;
  readonly onInViewCallback?: () => void;
} & Pick<ClassVariantsProps, "animation">;

export function FadeInIntersection({
  threshold = 0,
  thresholdMobile,
  children,
  className,
  asChild = false,
  animation,
  delay,
  style,
  rootMargin,
  onInViewCallback,
}: FadeInIntersectionProps) {
  const [thresholdValue, setThresholdValue] = useState(threshold);
  const [skip, setSkip] = useState(false);
  const { ref, inView } = useInView({
    skip,
    threshold: thresholdValue,
    triggerOnce: true,
    delay,
    rootMargin,
  });
  const Comp = asChild ? Slot.Root : "div";

  useDidMount(() => {
    setThresholdValue(getIsMobile() ? (thresholdMobile ?? threshold) : threshold);
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Ignore onInViewCallback
  useEffect(() => {
    if (inView) {
      setSkip(true);
      if (onInViewCallback) onInViewCallback();
    }
  }, [inView]);

  return (
    <Comp ref={ref} className={getFadeInIntersectionClass({ inView, animation, className })} style={style}>
      {children}
    </Comp>
  );
}
